(ns shared.core.style
  (:require [garden.core :refer [css]]
            [garden.stylesheet :refer [at-keyframes]]
            [clojure.string :as s]
            [shared.core.reactive :as re]
            [camel-snake-kebab.core :as csk]
            [shared.core.util :as util]
            [shared.core.amelia-light.components.theme]))

(defonce ^:private style-el* (atom nil))
(defonce hidden-scrollbar-class-name "sh-hidden-scrollbar")
(defonce block-scroll-class-name "sh-block-scroll")

(defn- style-el []
  (or @style-el*
      (let [el (.createElement js/document "style")]
        (set! (.-type el) "text/css")
        (.appendChild (.-head js/document) el)
        (reset! style-el* el)
        el)))

(defn add-link [href]
  (let [link (.createElement js/document "link")]
    (set! (.-rel link) "stylesheet")
    (set! (.-href link) href)
    (.appendChild (.-head js/document) link)))

(defn add-css
  [& rules]
  (let [s (if (string? (first rules))
            (s/join "\n" rules)
            (apply css rules))]
    (.appendChild
      (style-el)
      (.createTextNode js/document s))))

(defn add-raw-css [raw-css]
  (.appendChild
    (style-el)
    (.createTextNode js/document raw-css)))

(defn add-keyframes
  [identifier & frames]
  (add-css
    (apply at-keyframes identifier frames)))

(defn add-class-to-el [el class-name]
  (when (not-empty class-name)
    (some-> el
            (.-classList)
            (.add class-name))))

(defn remove-class-from-el [el class-name]
  (when (not-empty class-name)
    (some-> el
            (.-classList)
            (.remove class-name))))

(add-css
  [(str "." hidden-scrollbar-class-name) {:-ms-overflow-style "none"
                                          :scrollbar-width "none"}]
  [(str "." hidden-scrollbar-class-name "::-webkit-scrollbar") {:display "none"}]
  [(str "." block-scroll-class-name) {:overflow "hidden !important"}])

(defn block-scroll [el]
  (add-class-to-el el block-scroll-class-name))

(defn unblock-scroll [el]
  (remove-class-from-el el block-scroll-class-name))

(def colors {:gray {:base "#636569"
                    :shade-1 "#a3a4a7"
                    :shade-2 "#f1f1f1"
                    :shade-3 "#cecece"
                    :shade-4 "#303133"
                    :shade-5 "#404144"
                    :shade-6 "#5c5d5f"
                    :shade-7 "#ece9e9"
                    :shade-8 "#48494c"
                    :shade-9 "#f7f7f7"
                    :shade-10 "#011627"
                    :shade-11 "#fafafa"
                    :shade-12 "#fcfcfc"
                    :shade-13 "#d9d9d9"
                    :shade-14 "#e9e9e9"
                    :shade-15 "#f5f5f5"
                    :shade-16 "#727272"
                    :shade-17 "#ececec"
                    :shade-18 "#c5c5c5"
                    :shade-19 "#a7a7a7"
                    :shade-20 "#423f3c"
                    :shade-21 "#a5a5a5"
                    :shade-22 "#f4f4f4"
                    :shade-23 "#dadada"
                    :shade-24 "#E5E7EB"
                    :shade-25 "#e2e8f0"
                    :shade-26 "#64748b"
                    :shade-27 "#020717"
                    :shade-28 "#c2c2c2"
                    :shade-29 "#eff1f2"
                    :disabled "#bdbdbd"
                    :disabled-2 "#cacdd1"}
             :green {:base "#00Bc70"
                     :shade-1 "#096b43"
                     :shade-2 "#14ab6e"
                     :shade-3 "#e2f5ea"
                     :shade-4 "#d8f2e3"
                     :shade-5 "#047857"
                     :shade-6 "#d1fae5"
                     :shade-7 "#64cb4a"
                     :shade-8 "#4AC53F"
                     :shade-9 "#ADE3A8"}
             :red {:error "#d41414"
                   :base "#ee542f"
                   :shade-1 "#f3785b"
                   :shade-2 "#da4a27"
                   :shade-3 "#fee6e1"
                   :shade-4 "#fdded7"
                   :shade-5 "#f7e7ea"
                   :shade-6 "#f63535"
                   :shade-7 "#ff8080"}
             :yellow {:base "#f0f03f"
                      :shade-1 "#b7b700"
                      :shade-2 "#fbec5d"
                      :shade-3 "#d6d614"
                      :shade-4 "#ffd700"
                      :shade-5 "#ffb649"
                      :shade-6 "#ffc42c"}
             :orange {:base "#f2711c"
                      :shade-1 "#ffdbc3"
                      :shade-2 "#c2410c"
                      :shade-3 "#ffedd5"
                      :shade-4 "#ffdb99"}
             :blue {:base "#1cc1f2"
                    :shade-1 "#9933F0"
                    :shade-2 "#0279FF"
                    :shade-3 "#2962FF"}
             :black {:base "#000"
                     :shade-1 "#111827"
                     :shade-2 "#020717"
                     :shade-3 "#0c1a22"}
             :white {:base "#fff"}

             ;; SoundHound Styles
             :primary {:base "#a01958"
                       :darker-1 "#4a154b"}
             :on-primary {:base "#fff"}
             :background {:base "#fff"}
             :on-background {:base "#030303"}})

(def di-colors {:gray {:base "#2D2926"}})

(defn di-color [& path]
  (get-in di-colors path))

(defn color [& path]
  (get-in colors path))

(defn black-rgba [pct]
  (util/hex->rgba (color :black :base) pct))

(def theme-colors {:dark {:background {:base "#141918"
                                       :lighter-1 "#2a2f2e"
                                       :lighter-2 "#727877"
                                       :lighter-3 "#2c3432"}
                          :on-background {:base "#fafafa"
                                          :darker-1 "#eee9e9"}
                          :surface {:base "#424847"
                                    :darker-1 "#292d2c"
                                    :lighter-1 "#5c6160"
                                    :lighter-2 "#7b7f7f"}
                          :on-surface {:base "#d8dbdb"}
                          :primary {:base "#0ad1be"
                                    :darker-1 "#0fbdac"
                                    :lighter-1 "#d9fff9"
                                    :lighter-2 "#a5ebe0"}
                          :on-primary {:base "#121212"}
                          :secondary {:base "#ff6c9e"
                                      :darker-1 "#ff6c9e"
                                      :lighter-1 "#fdd5e3"}
                          :on-secondary {:base "#121212"}
                          :tertiary {:base "#c25aff"}
                          :on-tertiary {:base "#121212"}}})

(defn theme-color [theme path]
  (get-in theme-colors (concat [theme] path)))

(defn dark-color [& path]
  (theme-color :dark path))

(defn dark-prop [prop-val]
  {:dark prop-val})

(defn dark-style [style-val]
  (dark-prop {:style style-val}))

(def font-family* (atom "'Pangram', sans-serif"))
(defn set-font-family [ff] (reset! font-family* ff))
(def font-family @font-family*)
(def dm-sans-font-family "'DM Sans', sans-serif")
(def phosphor-font-family "'Phosphor'")
(def roboto-font-family "'Roboto', sans-serif")
(def inter-font-family "'Inter'")
(def material-icons-font-family "'Material Icons'")

(def font-extra-light-weight 100)
(def font-light-weight 300)
(def font-regular-weight 400)
(def font-medium-weight 500)
(def font-semi-bold-weight 600)
(def font-bold-weight 700)
(def font-extra-bold-weight 800)
(def font-black-weight 900)

(def hover-box-shadow "rgba(0, 0, 0, 0.07) 0px 3px 12px 0px")
(def sh-box-shadow "0 0 20px 0 rgba(222,228,233,0.51)")
(def sh-on-gray-box-shadow "rgb(172 172 172 / 38%) 0px 0px 20px 0px")
(def sh-primary-box-shadow "0 0 16px 0 rgba(160,25,88,0.3)")
(def di-box-shadow "0 0 20px 0 rgba(116, 116, 116, 0.35)")

(defn animate-css [k]
  (str "animate__animated animate__" (csk/->camelCaseString k)))

(defn z [k]
  (get {:low 1
        :medium 10
        :above-all 9999}
       k))

(defn sprite-style
  ([{:keys [file cols rows height width]} x-pos y-pos x-pad y-pad]
   {:background-image (str "url('" file "')")
    :background-size (str (* cols width) "px "
                          (* rows height) "px")
    :background-position (str (+ (* (- width) x-pos) x-pad) "px "
                              (+ (* (- height) y-pos) y-pad) "px")
    :height height
    :width width})
  ([args x-pos y-pos]
   (sprite-style args x-pos y-pos 0 0))
  ([args x-pos]
   (sprite-style args x-pos 0 0 0)))

(re/reg-event-db
  :style/import-stylesheet re/middleware
  (fn [db [stylesheet-url]]
    (let [head-el (.-head js/document)
          links-contains-href? (fn [url] (some #(= (str (.-href %)) url) (.getElementsByTagName head-el "link")))
          add-stylesheet (fn [{:keys [href]}]
                           (let [el (.createElement js/document "link")]
                             (set! (.-rel el) "stylesheet")
                             (set! (.-href el) href)
                             (.appendChild head-el el)
                             el))]
      (when-not (links-contains-href? stylesheet-url)
        (add-stylesheet {:href stylesheet-url}))
      db)))

(defn mix-color
  ([color pct] (str "color-mix(in srgb, " color " " pct "%, transparent)"))
  ([color pct base] (str "color-mix(in srgb, " color " " pct "%, " base ")")))

(defn alc-color
  ([k] (alc-color k @(re/subscribe [:alc/theme-])))
  ([k theme]
  (let [dark? (= theme :dark-mode)]
    (get {"Background/active" (mix-color "#991EFF" 40)
          "Background/active_c" (mix-color "#991EFF" 30)
          "Background/card_hex" (if dark? "#202224" "#fff")
          "Background/card_hex_secondary" (if dark? "#27292b" "#fff")
          "Background/alert_const" "#FFDB99"
          "Background/error_const" "#FF8080"
          "Background/success_const" "#ADE3A8"
          "Background/hover" (if dark? (mix-color "#fff" 10) (mix-color "#000" 5))
          "Background/page" (if dark? "#161717" "#f7f8fa")
          "Background/primary" (if dark? "#1F2224" "#fff")
          "Background/active_secondary" (if dark? (mix-color "#991EFF" 40) (mix-color "#991EFF" 20))
          "Background/active_secondary_hex" (if dark? "#592d8b" "#ebd3ff")
          "Background/secondary" (if dark? (mix-color "#fff" 5 "#202224") (mix-color "#B8C9E5" 20 "#fff"))
          "Background/tertiary" (if dark? (mix-color "#fff" 3) (mix-color "#B8C9E5" 5))
          "Background/up_level" (if dark? "#2F373D" "#FFFFFF")
          "Border/divider_secondary" (if dark? (mix-color "#fff" 10) (mix-color "#000" 10))
          "Border/chart_act" (if dark? (mix-color "#fff" 30) (mix-color "#000" 30))
          "Border/chart_norm" (if dark? (mix-color "#fff" 10) (mix-color "#000" 10))
          "Border/divider" (if dark? (mix-color "#fff" 30) (mix-color "#000" 30))
          "Border/none" (if dark? (mix-color "#fff" 0) (mix-color "#000" 0))
          "Border/primary" (if dark? (mix-color "#fff" 0) (mix-color "#000" 40))
          "Border/secondary_inp" (if dark? (mix-color "#fff" 20) (mix-color "#000" 20))
          "Border/secondary" (if dark? (mix-color "#fff" 0) (mix-color "#000" 20))
          "Buttons/ghost_normal" (if dark? (mix-color "#fff" 0) (mix-color "#000" 0))
          "Buttons/secondary_normal" (if dark? (mix-color "#fff" 10) (mix-color "#000" 0))
          "chart/blue" "#2594FB"
          "chart/green" "#19E39A"
          "chart/red" "#FB8286"
          "chart/turquoise" "#1CCBD6"
          "Chart/orange" "#FFB745"
          "Chart/orchid" "#B947EB"
          "Chart/tech_blue" "#00A8DD"
          "Chart/turquoise" "#1CCBD6"
          "Common/active" "#991EFF"
          "Common/error" (mix-color "#FF8080" 90)
          "Common/overlay" (if dark? (mix-color "#000" 80) (mix-color "#000" 30))
          "Common/success" (mix-color "#4AC53F" 80)
          "Common/White" "#fff"
          "extended[day]/chart/red" "#FB8286"
          "extended[day]/text/green" "#1FC710"
          "extended[night]/chart/green" "#19E39A"
          "Extended/orchid" "#B947EB"
          "Icons/primary" (if dark? (mix-color "#fff" 80) (mix-color "#000" 80))
          "Icons/primary_rich" (if dark? "#fff" "#000")
          "Icons/secondary" (if dark? (mix-color "#fff" 40) (mix-color "#000" 40))
          "Inputs/normal" (if dark? (mix-color "#000" 30) "#fff")
          "lines/chart_grid" (mix-color "#fff" 24)
          "Text/primary" (if dark? (mix-color "#fff" 80) (mix-color "#000" 80))
          "Text/primary_const" (mix-color "#000" 80)
          "Text/primary_header" (if dark? "#fff" "#000")
          "Text/primary_on_bg" (if dark? "#fff" "#fff")
          "Text/secondary" (if dark? (mix-color "#fff" 40) (mix-color "#000" 40))
          "Text/secondary_disabled" (if dark? (mix-color "#fff" 40) (mix-color "#000" 40))}
         k))))

(defn with-alc-style [{:keys [style] :as args}]
  (let [theme @(re/subscribe [:alc/theme-])]
    (merge style
           (get args theme))))

(defn alc-shadow [k]
  (get {"shadow/common_overlay" "0 2px 4px 0 rgba(0, 0, 0, 0.03), 0 6px 18px 0 rgba(0, 0, 0, 0.08)"}
       k))
